import { round } from 'components/utils';
import decimalsMask from './decimalsMask';
import { getPrecisionForUnit } from './columnMappingAccessors';

export const parseAP = (value) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

export const getAPUnit = (row, mappings) => row.LBX_unite_volume
  || row.LBX_volume_unit || mappings?.pureAlcoholVolumeAt20cResult?.unit;

export const summarizeAPTotal = (rows, mappings) => {
  const field = mappings?.pureAlcoholVolumeAt20cResult?.csvName;
  const valid = field ? rows.filter((row) => parseAP(row[field]) !== null) : [];
  const result = { totalCount: rows.length, includedCount: 0, missingCount: rows.length - valid.length };
  if (!field) return { ...result, reason: 'mapping' };
  if (!valid.length) return { ...result, reason: 'no_results' };
  const units = new Set(valid.map((row) => getAPUnit(row, mappings)));
  if ([...units].some((unit) => !unit)) return { ...result, reason: 'missing_unit' };
  if (units.size !== 1) return { ...result, reason: 'mixed_units' };
  const unit = [...units][0];
  const sum = valid.reduce((total, row) => total + Number(row[field]), 0);
  if (!Number.isFinite(sum)) return { ...result, reason: 'invalid_total' };
  return {
    ...result, includedCount: valid.length, unit, totalAP: round(sum, getPrecisionForUnit(unit, 2)),
  };
};

export const getAPDifference = (row, mappings) => {
  const theoreticalField = mappings?.pureAlcoholTheoretical?.csvName;
  const measuredField = mappings?.pureAlcoholVolumeAt20cResult?.csvName;
  if (!theoreticalField || !measuredField) return { reason: 'mapping' };
  const theoretical = parseAP(row[theoreticalField]);
  const measured = parseAP(row[measuredField]);
  if (theoretical === null || measured === null) {
    let reason = 'both';
    if (theoretical !== null) reason = 'measured';
    if (measured !== null) reason = 'theoretical';
    return { reason };
  }
  return { theoretical, measured, difference: theoretical - measured };
};

export const getDisplayedAPDifference = (row, mappings, fieldKey) => {
  const comparison = getAPDifference(row, mappings);
  if (comparison.reason) return '';
  const unit = getAPUnit(row, mappings);
  const decimals = decimalsMask[unit] ?? getPrecisionForUnit(unit, 2);
  if (fieldKey === 'evaporation') return round(comparison.difference, decimals);
  if (fieldKey === 'percentEvaporation') {
    return comparison.theoretical > 0
      ? round((comparison.difference / comparison.theoretical) * 100, decimals) : '';
  }
  return '';
};

export const summarizeAPDifference = (rows, mappings) => {
  const result = {
    comparableCount: 0,
    totalCount: rows.length,
    excluded: {
      theoretical: 0, measured: 0, both: 0, unit: 0,
    },
    losses: 0,
    gains: 0,
    theoretical: 0,
    measured: 0,
    unit: undefined,
  };
  rows.forEach((row) => {
    const comparison = getAPDifference(row, mappings);
    if (comparison.reason) {
      if (comparison.reason !== 'mapping') result.excluded[comparison.reason] += 1;
      return;
    }
    const unit = getAPUnit(row, mappings);
    if (!unit || (result.unit && result.unit !== unit)) {
      result.excluded.unit += 1;
      return;
    }
    if (!result.unit) result.unit = unit;
    result.comparableCount += 1;
    result.theoretical += comparison.theoretical;
    result.measured += comparison.measured;
    if (comparison.difference >= 0) result.losses += comparison.difference;
    else result.gains -= comparison.difference;
  });
  if (result.comparableCount === 0) return result;
  const { unit } = result;
  const precision = getPrecisionForUnit(unit, 2);
  const net = result.losses - result.gains;
  return {
    ...result,
    unit,
    totalLosses: round(result.losses, precision),
    totalGains: round(result.gains, precision),
    totalEvaporation: round(net, precision),
    totalTheoretical: round(result.theoretical, precision),
    totalMeasured: round(result.measured, precision),
    totalPercentEvaporation: result.theoretical ? round((net / result.theoretical) * 100, 1) : '',
  };
};
